/*
 * Copyright 2022 Larder Software Limited
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { createTemplateAction } from '@backstage/plugin-scaffolder-node';
import { resolveSafeChildPath } from '@backstage/backend-plugin-api';
import fs from 'fs-extra';
import YAML from 'yaml';

const parsers: Record<'yaml' | 'json' | 'multiyaml', (cnt: string) => any> = {
  yaml: (cnt: string) => YAML.parse(cnt),
  json: (cnt: string) => JSON.parse(cnt),
  multiyaml: (cnt: string) =>
    YAML.parseAllDocuments(cnt).map(doc => doc.toJSON()),
};

export function createParseFileAction() {
  return createTemplateAction({
    id: 'roadiehq:utils:fs:parse',
    description: 'Reads a file from the workspace and optionally parses it',
    supportsDryRun: true,
    schema: {
      input: {
        path: z => z.string().describe('Path to the file to read.'),
        parser: z =>
          z
            .enum(['yaml', 'json', 'multiyaml'])
            .optional()
            .describe('Optionally parse the content to an object.'),
      },
      output: {
        content: z =>
          z
            .union([z.string(), z.object({})])
            .describe('Content of the file')
            .optional(),
      },
    },
    async handler(ctx) {
      const sourceFilepath = resolveSafeChildPath(
        ctx.workspacePath,
        ctx.input.path,
      );
      const parserName = ctx.input.parser;
      let parser = (content: string) => content;

      if (parserName) {
        parser = parsers[parserName];
      }

      const content: string | object = parser(
        fs.readFileSync(sourceFilepath).toString(),
      );

      ctx.output('content', content);
    },
  });
}
